import { useTranslations } from '@vocab/react';
import { Column, Columns, Stack, Text } from 'braid-design-system';
import React, {
  forwardRef,
  type Ref,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { SECTION_PRIVATE_AD } from 'src/app/constants/pageSectionName';
import { PhoneNumber } from 'src/app/views/components/PhoneNumber';
import { PrivateAdToggle } from 'src/app/views/components/PrivateAdToggle';
import {
  privateAdFeatureSelector,
  usePrivateAdFeature,
} from 'src/app/views/hooks/usePrivateAdFeature';
import { EditablePageSection } from 'src/app/views/sections/EditablePageSection';
import type { PhoneNumberRef } from 'src/types/formFields';
import type { SectionProps, SectionRef } from 'src/types/sections';

import translations from './.vocab';

export const PrivateAdReadMode = () => {
  const { showPrivateAdToggle, showPhoneNumber } = usePrivateAdFeature();

  const { isConfidential, phoneNumber } = useSelector(
    privateAdFeatureSelector,
    shallowEqual,
  );

  const { t } = useTranslations(translations);

  return (
    <Stack space="large">
      {showPrivateAdToggle ? (
        <Columns space="small">
          <Column width="1/4">
            <Text weight="strong">{t('private-ad-label')}</Text>
          </Column>
          <Column>
            <Text>
              {isConfidential ? t('private-ad-true') : t('private-ad-false')}
            </Text>
          </Column>
        </Columns>
      ) : null}
      {showPhoneNumber ? (
        <Columns space="small">
          <Column width="1/4">
            <Text weight="strong">{t('private-ad-phone-number')}</Text>
          </Column>
          <Column>
            <Text>{phoneNumber}</Text>
          </Column>
        </Columns>
      ) : null}
    </Stack>
  );
};

interface EditProps {
  refs: {
    phoneNumberRef: RefObject<PhoneNumberRef>;
  };
}

const PrivateAdEditMode = (props: EditProps) => {
  const { showPrivateAdToggle, showPhoneNumber } = usePrivateAdFeature();

  return (
    <Stack space="medium">
      {showPrivateAdToggle ? <PrivateAdToggle /> : null}
      {showPhoneNumber ? <PhoneNumber ref={props.refs.phoneNumberRef} /> : null}
    </Stack>
  );
};

export const PrivateAdSection = forwardRef(
  (_props: SectionProps, ref: Ref<SectionRef>) => {
    const { showPhoneNumber } = usePrivateAdFeature();
    const { phoneNumber } = useSelector(privateAdFeatureSelector, shallowEqual);

    const [isEditingPhoneNumber, setIsEditingPhoneNumber] =
      useState<boolean>(false);

    useEffect(() => {
      const isPhoneNumberEmpty =
        phoneNumber === undefined || phoneNumber.length === 0;

      if (isPhoneNumberEmpty && !isEditingPhoneNumber) {
        const isPhoneNumberRequired = showPhoneNumber && isPhoneNumberEmpty;
        setIsEditingPhoneNumber(isPhoneNumberRequired);
      }
    }, [isEditingPhoneNumber, showPhoneNumber, phoneNumber]);

    const phoneNumberRef = useRef<PhoneNumberRef>(null);

    const { t } = useTranslations(translations);

    return (
      <EditablePageSection
        title={t('private-ad-section-title')}
        name={SECTION_PRIVATE_AD}
        ref={ref}
        fields={[phoneNumberRef]}
        read={<PrivateAdReadMode />}
        edit={
          <PrivateAdEditMode
            refs={{
              phoneNumberRef,
            }}
          />
        }
        defaultIsEdit={isEditingPhoneNumber}
      />
    );
  },
);
